import { Component, inject, ViewChild } from '@angular/core';

import { ProfileDropdownAvatarComponent, UserProfileMenuComponent } from '@mds/ui';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalStorageService } from '@services/local-storage.service';
import { GlobalDataService } from '@services/global-data.service';
import { Router } from '@angular/router';
import { userProfileRoute } from '@pages/dashboards/shared/utils/constants';
import { MdsIcon } from '@mds/icons';
import { MatDialog } from '@angular/material/dialog';
import { TeamSettingsComponent } from '@dialogs/team/team-settings/team-settings.component';
import { MakersiteService } from '@services/makersite.service';
import { CookiesService } from '@services/cookies.service';
import { LayoutModule } from '@angular/cdk/layout';
import { CreateTeamComponent } from '@dialogs/team/create-team/create-team.component';
import { AccountSettingsComponent } from '@dialogs/account-settings/account-settings.component';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-header-profile',
  standalone: true,
  imports: [
    ProfileDropdownAvatarComponent,
    UserProfileMenuComponent,
    LayoutModule,
    MatMenuTrigger,
    MatButton,
    MatMenu,
  ],
  templateUrl: './header-profile.component.html',
  styleUrls: ['./header-profile.component.scss'],
})
export class HeaderProfileComponent {
  @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;
  @ViewChild('userProfileMenu') menu: MatMenu;
  private localStorageService = inject(LocalStorageService);
  private globalDataService =  inject(GlobalDataService);
  userInfo = this.localStorageService.getItem('user_info');
  allFeatures = this.globalDataService.getFeatureArray();
  teamInfo = this.localStorageService.getItem('team_info');

  userProfileData = {
    profileDetails: {
      firstName: this.userInfo?.firstname,
      lastName: this.userInfo?.lastname,
      email: this.userInfo?.email,
      avatar: (this.userInfo?.avatar || []).includes('git_files') ? '' : this.userInfo?.avatar
    },
    menu: [
      {
        sectionTitle: 'account',
        items: [
          {
            key: userProfileRoute.profile.key,
            route: `${
              userProfileRoute.profile.route
            }/${this.userInfo.rid.replace('#', '')}`,
            title: 'View Profile',
            icon: MdsIcon.VIEW_PROFILE,
            showMenu: this.allFeatures.includes('user_profile'),
          },
          {
            key: userProfileRoute.accountSettings.key,
            title: 'Account Settings',
            icon: MdsIcon.ACCOUNT_SETTINGS,
            showMenu: true,
          },
        ],
      },
      {
        sectionTitle: 'tools',
        items: [
          {
            key: userProfileRoute.drive.key,
            route: userProfileRoute.drive.route,
            title: 'My Drive',
            icon: MdsIcon.MY_DRIVE,
            showMenu: this.allFeatures.includes('files'),
          },
          {
            key: userProfileRoute.todo.key,
            route: userProfileRoute.todo.route,
            title: 'To-Do',
            icon: 'todo',
            showMenu: this.allFeatures.includes('todos'),
          },
          {
            key: userProfileRoute.job.key,
            route: userProfileRoute.job.route,
            title: 'Jobs',
            icon: MdsIcon.JOB,
            showMenu: true,
          },
          {
            key: userProfileRoute.trash.key,
            route: userProfileRoute.trash.route,
            title: 'Trash',
            icon: MdsIcon.PROFILE_TRASH,
            showMenu: this.allFeatures.includes('trash'),
          },
          {
            key: userProfileRoute.dataUsage.key,
            route: userProfileRoute.dataUsage.route,
            title: 'Data usage statistics',
            icon: MdsIcon.DATA_USAGE,
            showMenu: this.allFeatures.includes('usage'),
          },
          {
            key: userProfileRoute.billing.key,
            route: userProfileRoute.billing.route,
            title: 'Billing',
            icon: MdsIcon.BILLING,
            showMenu: this.userInfo.plan === 'Admin',
          },
        ],
      },
      {
        sectionTitle: 'team',
        items: [
          {
            key: userProfileRoute.createTeam.key,
            title: 'Create Team',
            icon: MdsIcon.CREATE_TEAM,
            showMenu: this.allFeatures.includes('team'),
            // no route
          },
          {
            key: userProfileRoute.teamSettings.key,
            title: 'Team Settings',
            icon: MdsIcon.TEAM_SETTINGS,
            showMenu:
              this.teamInfo.team_rid && this.allFeatures.includes('team'),
            // no route
          },
        ],
      },
      {
        items: [
          {
            key: userProfileRoute.logout.key,
            title: 'Logout',
            icon: MdsIcon.LOGOUT,
            showMenu: true,
            // no route
          },
        ],
      },
    ],
  };

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private makersiteService: MakersiteService,
    private cookiesService: CookiesService
  ) {}

  menuOpened() {
    setTimeout(() => this.menu.focusFirstItem(), 0);
  }

  menuClickAction(menuKey: string) {
    switch (menuKey) {
      case userProfileRoute.profile.key:
        this.router.navigate([
          `${userProfileRoute.profile.route}/${this.userInfo.rid.replace(
            '#',
            ''
          )}`,
        ]);
        break;

      case userProfileRoute.accountSettings.key:
        this.openAccountSetting();
        break;

      case userProfileRoute.drive.key:
        this.router.navigate([userProfileRoute.drive.route]);
        break;

      case userProfileRoute.explorer.key:
        this.router.navigate([userProfileRoute.explorer.route]);
        break;

      case userProfileRoute.todo.key:
        this.router.navigate([userProfileRoute.todo.route]);
        break;

      case userProfileRoute.job.key:
        this.router.navigate([userProfileRoute.job.route]);
        break;

      case userProfileRoute.linker.key:
        this.router.navigate([userProfileRoute.linker.route]);
        break;

      case userProfileRoute.trash.key:
        this.router.navigate([userProfileRoute.trash.route]);
        break;

      case userProfileRoute.dataUsage.key:
        this.router.navigate([userProfileRoute.dataUsage.route]);
        break;

      case userProfileRoute.billing.key:
        this.router.navigate([userProfileRoute.billing.route]);
        break;

      case userProfileRoute.createTeam.key:
        this.openCreateTeam();
        break;

      case userProfileRoute.teamSettings.key:
        this.openTeamSetting();
        break;
      case userProfileRoute.logout.key:
        this.logout();
        break;

      default:
        break;
    }
  }

  openAccountSetting() {
    this.dialog.open(AccountSettingsComponent, {
      width: '50vw',
      height: '85vh',
    });
  }

  openCreateTeam() {
    this.dialog.open(CreateTeamComponent, {
      width: '50vw',
    });
  }

  openTeamSetting() {
    this.dialog.open(TeamSettingsComponent, {
      width: '50vw',
    });
  }

  logout() {
    this.makersiteService
      .logout()
      .pipe(
        catchError((err) => {
          const message = err.message || err;
          return of({ message });
        })
      )
      .subscribe(() => {
        this.localStorageService.clear();
        this.cookiesService.clearCookies();
        this.globalDataService.setPpmSelectedItem(null);
        this.globalDataService.setTokenString(null);
        this.globalDataService.setPPMLayer(null);
        this.globalDataService.setPpmId(null);
        this.router.navigate(['login']);
      });
  }
}
